import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, Globe, ArrowLeft, CheckCircle } from 'lucide-react'

export default function DNSManagementPage() {
  const navigate = useNavigate()
  const [zones, setZones] = useState([
    { id: 1, domain: 'example.com', status: 'Active', ttl: 3600, records: 24, lastUpdated: '2 hours ago', type: 'Public' },
    { id: 2, domain: 'internal.local', status: 'Active', ttl: 1800, records: 15, lastUpdated: '1 day ago', type: 'Internal' },
    { id: 3, domain: 'staging.example.com', status: 'Active', ttl: 300, records: 8, lastUpdated: '30 mins ago', type: 'Public' },
    { id: 4, domain: 'api.example.com', status: 'Active', ttl: 600, records: 5, lastUpdated: '3 days ago', type: 'Public' },
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingZone, setEditingZone] = useState(null)
  const [formData, setFormData] = useState({ domain: '', ttl: 3600, type: 'Public' })

  const stats = useMemo(() => ({
    totalZones: zones.length,
    activeZones: zones.filter(z => z.status === 'Active').length,
    totalRecords: zones.reduce((sum, z) => sum + z.records, 0),
    publicZones: zones.filter(z => z.type === 'Public').length
  }), [zones])

  const handleCreate = () => {
    setEditingZone(null)
    setFormData({ domain: '', ttl: 3600, type: 'Public' })
    setShowModal(true)
  }

  const handleEdit = (zone) => {
    setEditingZone(zone)
    setFormData({ domain: zone.domain, ttl: zone.ttl, type: zone.type })
    setShowModal(true)
  }

  const handleSave = () => {
    if (!formData.domain) return alert('Domain required')
    if (editingZone) {
      setZones(zones.map(z => z.id === editingZone.id ? { ...z, ...formData } : z))
    } else {
      setZones([...zones, { id: Date.now(), ...formData, status: 'Active', records: 0, lastUpdated: 'Just now' }])
    }
    setShowModal(false)
  }

  const handleDelete = (id) => {
    if (confirm('Delete this DNS zone?')) {
      setZones(zones.filter(z => z.id !== id))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/network')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Network Management
        </button>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">DNS Management</h1>
              <p className="text-slate-400">DNS filtering, blocking, and optimization</p>
            </div>
            <button onClick={handleCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
              <Plus size={20} /> Add Zone
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Zones</div>
            <div className="text-3xl font-bold text-blue-400">{stats.totalZones}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active Zones</div>
            <div className="text-3xl font-bold text-green-400">{stats.activeZones}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Records</div>
            <div className="text-3xl font-bold text-purple-400">{stats.totalRecords}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Public Zones</div>
            <div className="text-3xl font-bold text-cyan-400">{stats.publicZones}</div>
          </div>
        </div>

        <div className="space-y-4">
          {zones.map((zone) => (
            <div key={zone.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Globe className="text-blue-400" size={20} />
                    <h3 className="text-lg font-semibold text-white">{zone.domain}</h3>
                    <span className="px-3 py-1 rounded text-xs font-semibold bg-green-600/20 text-green-400">{zone.status}</span>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${zone.type === 'Public' ? 'bg-blue-600/20 text-blue-400' : 'bg-purple-600/20 text-purple-400'}`}>{zone.type}</span>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">TTL</p>
                      <p className="text-white font-bold mt-1">{zone.ttl}s</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Records</p>
                      <p className="text-white font-bold mt-1">{zone.records}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3 col-span-2">
                      <p className="text-slate-400 text-xs">Last Updated</p>
                      <p className="text-white font-bold mt-1">{zone.lastUpdated}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(zone)} className="p-2 hover:bg-blue-500/20 rounded">
                    <Edit2 size={18} className="text-blue-400" />
                  </button>
                  <button onClick={() => handleDelete(zone.id)} className="p-2 hover:bg-red-500/20 rounded">
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 mt-8">
          <h3 className="text-lg font-bold text-white mb-4">DNS Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-700/30 rounded p-4">
              <p className="text-white font-semibold mb-2">Primary Nameserver</p>
              <p className="text-slate-400 text-sm font-mono">ns1.example.com (8.8.8.8)</p>
            </div>
            <div className="bg-slate-700/30 rounded p-4">
              <p className="text-white font-semibold mb-2">Secondary Nameserver</p>
              <p className="text-slate-400 text-sm font-mono">ns2.example.com (8.8.4.4)</p>
            </div>
            <div className="bg-slate-700/30 rounded p-4">
              <p className="text-white font-semibold mb-2">DNSSEC Status</p>
              <p className="text-green-400 text-sm font-semibold">Enabled</p>
            </div>
            <div className="bg-slate-700/30 rounded p-4">
              <p className="text-white font-semibold mb-2">Query Logging</p>
              <p className="text-blue-400 text-sm font-semibold">Active</p>
            </div>
          </div>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">{editingZone ? 'Edit DNS Zone' : 'Add DNS Zone'}</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.domain} onChange={(e) => setFormData({...formData, domain: e.target.value})} placeholder="Domain Name" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="number" value={formData.ttl} onChange={(e) => setFormData({...formData, ttl: parseInt(e.target.value)})} placeholder="TTL (seconds)" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                  <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>Public</option>
                    <option>Internal</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSave} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
